import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'formik';
import { useLocalStorage } from '../../../../../hooks';
import AdGroupListTable from '../../../../molecules/AdGroupListTable';
import { conditionBuilder } from '../../../../../graphql';
const { search, filter } = conditionBuilder;
import { ResourceType } from '../../../../../enums/PeriodBudgetResource';
import { RESOURCE_COUNT_LIMIT } from '../../../Form';
import { omitProps } from '../../../../../lib/objectUtils';

const propTypes = {
  formik: PropTypes.object,
};

const defaultProps = {};

const AdGroupTabContent = ({
  formik: {
    values: { projectId, resources },
    setFieldValue,
  },
}) => {
  const [initialTableState, saveInitialTableState] = useLocalStorage(
    '/projects/period_budget_reports/form/ad_group_tab/table_state',
    { pagination: { pageIndex: 0, pageSize: 25 } }
  );

  const tableState = useMemo(
    () => ({
      rowSelection: resources.reduce((selection, resource) => {
        if (resource.resourceType == ResourceType.AdGroup) {
          selection[resource.resourceId] = true;
        }
        return selection;
      }, {}),
    }),
    [JSON.stringify(resources)]
  );

  const handleStateChange = ({ pagination, ...state }) =>
    saveInitialTableState({
      ...omitProps(
        state,
        'rowSelection', // リソースの選択状態は保存しない
        'globalFilter' // 検索状態は保存しない
      ),
      pagination: {
        ...pagination,
        pageIndex: 0, // 表示中のページ数は保存しない
      },
    });

  const handleFetch = useCallback(
    async (query, variables) => {
      return await query({
        ...variables,
        search: search.and([
          variables.search,
          filter.eq('projectId', projectId),
          filter.eq('adAccount.active', true),
          filter.eq('adCampaign.active', true),
        ]),
      });
    },
    [projectId]
  );

  const handleToggleSelected = useCallback(
    (adGroup) => {
      const nextResources = [...resources];

      const index = nextResources.findIndex(
        ({ resourceType, resourceId }) =>
          resourceType == ResourceType.AdGroup && resourceId == adGroup.id
      );
      if (index < 0) {
        nextResources.push({
          resourceType: ResourceType.AdGroup,
          resourceId: adGroup.id,
          channelId: adGroup.channel.id,
          adAccountId: adGroup.adAccount.id,
          adCampaignId: adGroup.adCampaign.id,
          adGroupId: adGroup.id,
        });

        let childIndex = -1;
        while (
          0 <=
          (childIndex = nextResources.findIndex(
            (r) =>
              r.resourceType != ResourceType.AdGroup &&
              r.adGroupId == adGroup.id
          ))
        ) {
          nextResources.splice(childIndex, 1);
        }
      } else {
        nextResources.splice(index, 1);
      }

      setFieldValue('resources', nextResources);
    },
    [JSON.stringify(resources)]
  );

  const isParentSelected = useCallback(
    (adGroup) => {
      const parentIndex = resources.findIndex(
        (r) =>
          (r.resourceType == ResourceType.AdAccount &&
            r.resourceId == adGroup.adAccount.id) ||
          (r.resourceType == ResourceType.AdCampaign &&
            r.resourceId == adGroup.adCampaign.id)
      );
      return 0 <= parentIndex;
    },
    [JSON.stringify(resources)]
  );

  return (
    <AdGroupListTable
      responsive
      initialState={initialTableState}
      state={tableState}
      isParentSelected={isParentSelected}
      enableNewRowSelection={resources.length < RESOURCE_COUNT_LIMIT}
      onStateChange={handleStateChange}
      onFetch={handleFetch}
      onToggleSelected={handleToggleSelected}
    />
  );
};

AdGroupTabContent.propTypes = propTypes;
AdGroupTabContent.defaultProps = defaultProps;

export default connect(AdGroupTabContent);
